import { useState, useEffect, useCallback } from 'react';
import { patientService } from '../services/patientService';
import { PatientFormData } from '../types';

const initialFormData: PatientFormData = {
  recordNumber: '',
  isActive: true,
  identificationType: 1,
  identification: '',
  gender: true,
  birthDate: '',
  firstName: '',
  lastName: '',
  nationalityId: 0,
  provinceId: null,
  municipalDistrictId: null,
  address: '',
  email: '',
  phone: '',
  mobile: '',
  insurances: [],
};

export const usePatientDetail = (patientId?: number) => {
  const [formData, setFormData] = useState<PatientFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!patientId);

  // Load patient data if editing
  useEffect(() => {
    if (!patientId) {
      setInitialLoading(false);
      return;
    }

    const loadPatient = async () => {
      setInitialLoading(true);
      try {
        const patient = await patientService.getWithHistory(patientId);

        setFormData({
          recordNumber: patient.medical_record_number || '',
          isActive: patient.status === 1 || patient.status === '1',
          identificationType: patient.identification_type_id || 1,
          identification: patient.identification || '',
          gender: patient.gender ?? true,
          birthDate: patient.birth_date ? patient.birth_date.slice(0, 10) : '',
          firstName: patient.first_name || '',
          lastName: patient.last_name || '',
          nationalityId: patient.nationality_id || 0,
          provinceId: patient.province_id || null,
          municipalDistrictId: patient.municipal_district_id || null,
          address: patient.address || '',
          email: patient.email || '',
          phone: patient.phone || '',
          mobile: patient.mobile || '',
          insurances: patient.insurances?.map((ins: any) => ({
            id: ins.id,
            policyNumber: ins.policy_number,
            nss: ins.nss,
            cardNumber: ins.card_number,
            insurancePlan: {
              id: ins.insurance_company_plan_id,
              insurance: { name: ins.insuranceCompanyPlan?.company?.name || ins.company?.name || '', description: '' },
              plan: { name: ins.insuranceCompanyPlan?.plan?.name || ins.plan?.name || '' },
            },
          })) || [],
        });
      } catch (err) {
        console.error('Error loading patient:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    loadPatient();
  }, [patientId]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.recordNumber.trim()) newErrors.recordNumber = 'El número de expediente es obligatorio';
    if (!formData.identification.trim()) newErrors.identification = 'La identificación es obligatoria';
    if (!formData.firstName.trim()) newErrors.firstName = 'El nombre es obligatorio';
    if (!formData.lastName.trim()) newErrors.lastName = 'El apellido es obligatorio';
    if (!formData.birthDate) newErrors.birthDate = 'La fecha de nacimiento es obligatoria';
    if (formData.nationalityId === 0) newErrors.nationalityId = 'La nacionalidad es obligatoria';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        medical_record_number: formData.recordNumber,
        status: formData.isActive ? '1' : '0',
        identification_type_id: formData.identificationType,
        identification: formData.identification,
        gender: formData.gender,
        birth_date: formData.birthDate,
        first_name: formData.firstName,
        last_name: formData.lastName,
        nationality_id: formData.nationalityId,
        province_id: formData.provinceId,
        municipal_district_id: formData.municipalDistrictId,
        address: formData.address,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile,
      };

      await patientService.createOrUpdate(payload, patientId);
      alert(patientId ? 'Paciente actualizado correctamente' : 'Paciente creado correctamente');
      return true;
    } catch (error: any) {
      console.error('Error saving patient:', error);
      alert('Error al guardar el paciente');
      return false;
    } finally {
      setSaving(false);
    }
  }, [formData, patientId, validateForm]);

  const handleInputChange = useCallback((field: keyof PatientFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    loading: initialLoading,
    saving,
    handleSubmit,
    handleInputChange,
    resetForm,
    setFormData,
  };
};
